import { useEffect, useMemo } from 'react';
import * as Y from 'yjs';
import { getShapesMap, LOCAL_ORIGINS } from './binding/shapesMap';

// Wire `Y.UndoManager` to the shapes map so the local user can undo / redo
// their own moves, edits, creates, and deletes — but never a peer's. The
// origin set is the source of truth for what counts as "mine"; remote
// updates (provider default origin) and seed writes get ignored.
//
// `captureTimeout` (default 500ms) coalesces rapid same-origin transactions
// into one undo step. That keeps a 60fps drag = one undo step, and rapid
// typing in the text editor = one undo step per pause.
export function useUndoManager(doc: Y.Doc | null): Y.UndoManager | null {
  const manager = useMemo(() => {
    if (!doc) return null;
    return new Y.UndoManager(getShapesMap(doc), {
      trackedOrigins: new Set(LOCAL_ORIGINS),
      captureTimeout: 500,
    });
  }, [doc]);

  useEffect(() => {
    return () => {
      manager?.destroy();
    };
  }, [manager]);

  return manager;
}

// Bind Cmd/Ctrl+Z and Cmd/Ctrl+Shift+Z (or Cmd/Ctrl+Y on Windows pattern)
// to undo/redo. Listener installed on `window`; ignored while the user is
// typing in a real DOM input/textarea so the browser's native undo wins
// inside the text editor overlay.
export function useUndoShortcuts(manager: Y.UndoManager | null) {
  useEffect(() => {
    if (!manager) return;
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const target = e.target as HTMLElement | null;
      if (target && isTextInput(target)) return;

      const key = e.key.toLowerCase();
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = (key === 'z' && e.shiftKey) || key === 'y';
      if (!isUndo && !isRedo) return;

      e.preventDefault();
      if (isUndo) manager.undo();
      else manager.redo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [manager]);
}

function isTextInput(el: HTMLElement): boolean {
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return type === 'text' || type === 'search' || type === 'email' || type === 'url';
  }
  return el.isContentEditable;
}
